import React from 'react';
import { useBroadcastCompleteModal } from './useBroadcastCompleteModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as SharedStyled from '@common/styles';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import DatapharmTable from '@common/components/DatapharmTable/DatapharmTable';
import { Section } from '@common/components/Section/Section';
import { HtmlContentModal } from '@common/components/HtmlContentModal/HtmlContentModal';
import { BroadcastCompleteModal } from '@common/components';
import { Button } from '@common/components/Button/Button';
import { ButtonTypes } from '@common/components/Button/types';
import { routes } from '../../../routing/routes';
import { IBroadcastRecipient, OptedInStatus } from '../types';
import { fetchBroadcastPreview } from '../../../services/broadcastService';
import { ColumnDef } from '@tanstack/react-table';
import { useFetchSubmissionTaskData } from '@common/hooks/useFetchSubmissionTaskData';
import { useFetchTaskProductGroupData } from '@common/features/submissionTask/hooks/useFetchTaskProductGroupData';
import * as Styled from './styles';
import { useHtmlContentModal } from '@common/components';
import { useSendBroadcast } from './hooks/useSendBroadcast';

const BroadcastConfirmation = () => {
  const handleBack = () => {
    navigate(`${routes.changeBroadcast}/${taskId}`, {
      state: { selectedRecipientIds },
    });
  };
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { htmlContentModalParams, closeHtmlContentModal, handleViewContent } = useHtmlContentModal();
  const { sendBroadcast, isSending } = useSendBroadcast();


  const {
    showBroadcastCompleteModal,
    broadcastResponse,
    openBroadcastCompleteModal,
    closeBroadcastCompleteModal,
    } = useBroadcastCompleteModal();

  // Get recipients from navigation state (already filtered to selected recipients)
  const state = location.state as { recipients?: IBroadcastRecipient[]; selectedRecipientIds?: string[] };
  const selectedRecipients = state?.recipients || [];
  const selectedRecipientIds = state?.selectedRecipientIds || [];

  // Fetch task data to get the title
  const { submissionTask } = useFetchSubmissionTaskData(taskId || '');
  const { productGroupWithTaskApplied } = useFetchTaskProductGroupData(taskId || '');

  const taskTitle = productGroupWithTaskApplied?.productGroup.productGroupName
    ? `${submissionTask?.submissionId}/${submissionTask?.submissionTaskId}_${productGroupWithTaskApplied?.productGroup.productGroupName}`
    : 'Loading...';

  const handleSend = async () => {
    if (!taskId) return;

    try {
      // Actually send the broadcast via API
      await sendBroadcast({ taskId, recipientIds: selectedRecipientIds });

      // Calculate counts based on selected recipients
      const deliveredCount = selectedRecipients.filter((r) => r.status === OptedInStatus.Yes).length;
      const queuedCount = selectedRecipients.length - deliveredCount;

      openBroadcastCompleteModal({
        totalRecipients: selectedRecipients.length,
        deliveredCount,
        queuedCount,
      });
    } catch (error) {
      closeBroadcastCompleteModal();
    }
  };

  const handleViewRecipients = () => {
    closeBroadcastCompleteModal();
    navigate(`${routes.changeBroadcast}/${taskId}`, {
      state: { selectedRecipientIds },
    });
  };

  const handleBackToSubmissions = () => {
    closeBroadcastCompleteModal();
    navigate(routes.submissions);
  };

  const handleViewHtmlClick = async () => {
    if (!taskId) return;

    const data = await fetchBroadcastPreview(taskId);
    handleViewContent(data, 'Email Preview');
  };

  // Table columns for recipient summary
  const summaryColumns: ColumnDef<IBroadcastRecipient>[] = [
    {
      accessorKey: 'email',
      header: 'Email address',
      cell: (info) => <span>{info.getValue() as string}</span>,
    },
  ];

  return (
    <>
      <SharedStyled.SubmissionFlowWrap>
        <SharedStyled.SubmissionFlowInner>
          <SharedStyled.PageTitle>Send broadcast</SharedStyled.PageTitle>
          <SharedStyled.PageSubTitle>Review the recipients below and confirm to send the broadcast</SharedStyled.PageSubTitle>

          <Styled.SectionsWrap>
            <Section text="Recipient list">
              <Styled.BroadcastTitle>Broadcast changes to {taskTitle}</Styled.BroadcastTitle>

              <Styled.TableWrapper>
                <DatapharmTable columns={summaryColumns} documents={selectedRecipients} />
              </Styled.TableWrapper>
            </Section>
          </Styled.SectionsWrap>
        </SharedStyled.SubmissionFlowInner>
      </SharedStyled.SubmissionFlowWrap>

      <SubmissionFlowFooter
        continueText={isSending ? 'Sending...' : 'Send broadcast'}
        onContinue={handleSend}
        backText='Back'
        onBack={handleBack}
        customRightContent={
          <Button
            key="preview-email-button"
            type={ButtonTypes.PRIMARY_BORDER}
            icon="/icons/link.svg"
            text="Preview email"
            onClick={handleViewHtmlClick}
          />
        }
      />

      <HtmlContentModal modalParams={htmlContentModalParams} onClose={() => closeHtmlContentModal()} width='80%' cancelText='Close' />

      {broadcastResponse && (
        <BroadcastCompleteModal
          isOpen={showBroadcastCompleteModal}
          recipientCount={broadcastResponse.totalRecipients}
          deliveredCount={broadcastResponse.deliveredCount}
          queuedCount={broadcastResponse.queuedCount}
          onViewRecipients={handleViewRecipients}
          onBackToSubmissions={handleBackToSubmissions}
        />
      )}

    </>
  );
}

export default BroadcastConfirmation;
